import { ALL_COLUMNS } from 'app/KinerjaShared/components/PenetapanPredikatTable/constants'
import { formatDate } from 'utils/datetime'
import { getTargetInfo } from 'app/KinerjaShared/components/CoachMarkV2'

import type {
  ColumnConfig,
  ColumnKey,
} from 'app/KinerjaShared/components/PenetapanPredikatTable/type'

// Helper function to create custom column configurations
export function createTableColumns(
  overrides: Partial<Record<ColumnKey, Partial<ColumnConfig>>> = {}
): ColumnConfig[] {
  return ALL_COLUMNS.map((col) => ({
    ...col,
    ...overrides[col.key],
  }))
}

// Helper function to show only specific columns
export function showOnlyColumns(columnsToShow: ColumnKey[]): ColumnConfig[] {
  return ALL_COLUMNS.map((col) => ({
    ...col,
    visible: columnsToShow.includes(col.key),
  }))
}

export function generateCsvFileName(prefix?: string): string {
  return `${prefix} - ${formatDate(new Date(), 'ddmmyyyy')}.csv`
}

/**
 * Calculates height for onboarding/tutorial display by calculating viewport constraints
 * @param tourRoot - The root element for the tour
 * @param mainTargetSelector - CSS selector for the main target element (usually the table)
 * @param footerSelector - CSS selector for the footer element that may clip the table
 * @returns ElementInfo object with adjusted dimensions
 */
export function generateClipTableHeightForOnboarding(
  tourRoot: Element,
  mainTargetSelector: string,
  footerSelector: string
) {
  const mainTarget: HTMLElement = tourRoot.querySelector(mainTargetSelector)
  // the table may be clipped by the footer element,
  // so we need to adjust the height of the table to fit the viewport
  const footerTarget: HTMLElement = tourRoot.querySelector(footerSelector)

  const previousTargetInfo = getTargetInfo(tourRoot, mainTarget)

  // get heights of the footer
  const footerHeight = footerTarget?.offsetHeight || 0

  // get the y position of the table
  const tableY = mainTarget.getBoundingClientRect().y

  // calculate the new height for the table
  const newHeight = window.innerHeight - tableY - footerHeight

  return {
    ...previousTargetInfo,
    dims: {
      ...previousTargetInfo.dims,
      // set the height to the new height, but not exceeding the previous height
      // if the table hasn't clipped the footer elements
      height: Math.min(newHeight, previousTargetInfo.dims.height),
    },
  }
}
